import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Minus, TrendingDown, Info } from 'react-feather';
import { Popover } from 'antd';
import axios from 'axios';

const TransportStatistics = () => {
    const [statistics, setStatistics] = useState({
        totalDeliveries: 0,
        totalRevenue: 0,
        totalRounds: 0,
        totalExpenses: 0,
        netProfit: 0,
        activeTransports: 0,
        averageDeliveryPrice: 0,
        loading: true
    });

    const [transports, setTransports] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [selectedTransport, setSelectedTransport] = useState('all');

    useEffect(() => {
        fetchTransportStatistics();
    }, [selectedMonth, selectedTransport]);

    const fetchTransportStatistics = async () => {
        try {
            setStatistics(prev => ({ ...prev, loading: true }));
            
            // Prepare parameters for the new backend endpoint
            const params = {};
            
            // Add date filters
            if (selectedMonth !== 'all') {
                params.from_date = `${selectedMonth}-01`;
                params.to_date = `${selectedMonth}-31`;
            }
            
            // Add transport filter
            if (selectedTransport !== 'all') {
                params.transport_id = selectedTransport;
            }

            const response = await axios.get('/dashboard/transport-statistics', { params });
            const { data, transports: transportOptions } = response.data;

            // Set transports for dropdown (only on first load)
            if (transports.length === 0) {
                setTransports(transportOptions || []);
            }

            setStatistics({
                totalDeliveries: data.total_deliveries,
                totalRevenue: data.total_revenue,
                totalRounds: data.total_rounds,
                totalExpenses: data.total_expenses,
                netProfit: data.net_profit,
                activeTransports: data.active_transports,
                averageDeliveryPrice: data.average_delivery_price,
                loading: false
            });
        } catch (error) {
            console.error('Error fetching transport statistics:', error);
            setStatistics(prev => ({ ...prev, loading: false }));
        }
    };

    const formatNumber = (num) => {
        const absNum = Math.abs(num);
        if (absNum >= 1000000) {
            return (num / 1000000).toFixed(1) + ' mln';
        } else if (absNum >= 1000) {
            return (num / 1000).toFixed(1) + ' ming';
        }
        return num.toString();
    };

    const cards = [
        {
            title: 'Jami reys soni',
            value: statistics.totalRounds,
            icon: TrendingUp,
            color: 'info',
            suffix: 'ta',
            info: "Tanlangan davrdagi barcha reyslar yig'indisi"
        },
        {
            title: 'Jami daromad',
            value: formatNumber(statistics.totalRevenue),
            icon: DollarSign,
            color: 'success',
            suffix: ' so\'m',
            info: "Barcha reyslarning (reys soni * narxi) yig'indisi"
        },
        {
            title: 'Harajatlar',
            value: formatNumber(statistics.totalExpenses),
            icon: Minus,
            color: 'danger',
            suffix: ' so\'m',
            info: "Transport ehtiyot qismlari + Umumiy transport xarajatlari"
        },
        {
            title: 'Sof foyda',
            value: formatNumber(statistics.netProfit),
            icon: statistics.netProfit >= 0 ? TrendingUp : TrendingDown,
            color: statistics.netProfit >= 0 ? 'success' : 'danger',
            suffix: ' so\'m',
            info: "Jami daromad - Harajatlar"
        }
    ];

    if (statistics.loading) {
        return (
            <div className="card">
                <div className="card-header">
                    <h5 className="card-title mb-0">Transport statistikasi</h5>
                </div>
                <div className="card-body">
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <div className="row align-items-center">
                    <div className="col">
                        <h5 className="card-title mb-0">Transport statistikasi</h5>
                    </div>
                    <div className="col-auto">
                        <div className="d-flex gap-2">
                            <select 
                                className="form-select"
                                value={selectedTransport}
                                onChange={(e) => setSelectedTransport(e.target.value)}
                                style={{minWidth: '150px'}}
                            >
                                <option value="all">Barcha transportlar</option>
                                {transports.map(transport => (
                                    <option key={transport.id} value={transport.id}>
                                        {transport.label}
                                    </option>
                                ))}
                            </select>
                            <select 
                                className="form-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                style={{minWidth: '150px'}}
                            >
                                <option value="all">Hammasi</option>
                                {Array.from({ length: 12 }, (_, i) => {
                                    const date = new Date();
                                    date.setMonth(date.getMonth() - i);
                                    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                    
                                    // Uzbek month names
                                    const uzbekMonths = [
                                        'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
                                        'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'
                                    ];
                                    
                                    const label = `${date.getFullYear()} ${uzbekMonths[date.getMonth()]}`;
                                    
                                    return (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="row">
                    {cards.map((card, index) => (
                        <div key={index} className="col-12 col-sm-6 col-lg-3 mb-3">
                            <div className={`card border-${card.color} h-100`} style={{position: 'relative'}}>
                                <Popover content={card.info} title={card.title} trigger="click">
                                    <Info size={14} className="text-muted" style={{position: 'absolute', top: 8, right: 8, cursor: 'pointer'}} />
                                </Popover>
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <card.icon className={`feather text-${card.color}`} size={24} />
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <h6 className="card-title mb-0 text-muted">{card.title}</h6>
                                            <h4 className="mt-1 mb-0">
                                                {card.value}{card.suffix}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TransportStatistics; 